/* eslint-disable react/jsx-no-useless-fragment */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  borderRadius,
  Button,
  colors,
  Heading,
  HStack,
  sizes,
  VStack,
} from '@rtkwlf/fenrir-react';
import { faSortAmountDown } from '@rtkwlf/pro-regular-svg-icons/faSortAmountDown';
import { faSortAmountUpAlt } from '@rtkwlf/pro-regular-svg-icons/faSortAmountUpAlt';
import type { TicketComment } from '@rtkwlf/rendall';
import { Trans, useTranslation } from 'react-i18next';

import { CommentTile } from './commentTile/CommentTile';

const Separator = styled.hr({
  border: `1px solid rgb(${colors.border.neutral.secondary})`,
  marginBottom: sizes['1.5rem'],
  width: ' 100%',
});

const FieldSet = styled.fieldset({
  border: `1px solid rgb(${colors.border.neutral.secondary})`,
  borderRadius: borderRadius.default,
  height: 'fix-content',
  minWidth: '0',
  width: '-webkit-fill-available',
});

const SORT_NEWEST = 'new';
const SORT_OLDEST = 'old';

type SortState = 'new' | 'old';

type CommentsProps = {
  comments: Array<TicketComment>;
};

export const TicketComments: React.FC<CommentsProps> = ({ comments }) => {
  const { t } = useTranslation();
  const [commentsSortOrder, setCommentsSortOrder] =
    useState<SortState>(SORT_NEWEST);
  const [commentsData, setCommentsData] = useState(comments);

  useEffect(() => setCommentsData(comments), [comments]);

  const handleCommentsSortOrder = useCallback(() => {
    const sortOrder =
      commentsSortOrder === SORT_NEWEST ? SORT_OLDEST : SORT_NEWEST;

    setCommentsSortOrder(sortOrder);
  }, [commentsSortOrder]);

  const sortedData = useMemo(() => {
    const data = commentsData.sort(
      (a, b) =>
        new Date(a.createdAt as string).getTime() -
        new Date(b.createdAt as string).getTime(),
    );
    if (commentsSortOrder === SORT_NEWEST) {
      data.reverse();
    }

    return data;
  }, [commentsSortOrder, commentsData]);

  if (!sortedData.length) {
    return null;
  }

  return (
    <>
      {/* this will be enable once we are ready with others tabs implementation eg: ticket history
      <HStack
        position="relative"
        width="full"
        marginBottom="small"
        xAlign="between"
        yAlign="center"
      >
        <Heading
          level="3"
          paddingBottom="default"
          borderWidth="zero"
          borderBottomWidth="xlarge"
          borderStyle="solid"
          borderColor="brandDefault"
        >
          {t('ticket:commentTitle')}
        </Heading>
        <Button
          variant="link"
          onClick={handleCommentsSortOrder}
          iconRight={
            <FontAwesomeIcon
              icon={
                commentsSortOrder === SORT_OLDEST
                  ? faSortAmountUpAlt
                  : faSortAmountDown
              }
              css={css`
                font-size: 14px;
                color: rgb(${colors.background.brand.default});
              `}
            />
          }
        >
          {commentsSortOrder === SORT_NEWEST
            ? t('ticket:newestFirstSort')
            : t('ticket:oldestFirstSort')}
        </Button>
      </HStack> */}
      <VStack gap="large" width="full">
        <FieldSet>
          <legend>
            <Heading level="3">
              <Trans
                i18nKey="ticket:details.comments"
                components={[<>{{ commentsCount: sortedData.length }}</>]}
              />
            </Heading>
          </legend>
          <HStack
            position="relative"
            xAlign="between"
            yAlign="center"
            justifyContent="flex-end"
            padding="default"
          >
            <Button
              variant="link"
              onClick={handleCommentsSortOrder}
              iconRight={
                <FontAwesomeIcon
                  icon={
                    commentsSortOrder === SORT_OLDEST
                      ? faSortAmountUpAlt
                      : faSortAmountDown
                  }
                  css={css({
                    color: `rgb(${colors.background.brand.default})`,
                    fontSize: '14px',
                  })}
                />
              }
            >
              {commentsSortOrder === SORT_NEWEST
                ? t('ticket:newestFirstSort')
                : t('ticket:oldestFirstSort')}
            </Button>
          </HStack>
          <VStack padding="medium" gap="small">
            {sortedData.map((comment, index) => (
              <React.Fragment key={comment.id}>
                <CommentTile
                  user={comment.requester}
                  commentTime={comment.createdAt}
                  description={comment.description}
                  requesterAgent={comment.requesterAgent}
                  attachments={comment.attachments}
                />
                {index < sortedData.length - 1 && <Separator />}
              </React.Fragment>
            ))}
          </VStack>
        </FieldSet>
      </VStack>
    </>
  );
};
